import format from "date-fns/format";
import parseISO from "date-fns/parseISO";
import { DateFormatOptions, DateFormatProps } from "./types";
import compareAsc from "date-fns/compareAsc";
import isValid from "date-fns/isValid";
import add from "date-fns/add";
import formatISO9075 from "date-fns/formatISO9075";
import differenceInCalendarDays from "date-fns/differenceInCalendarDays";
import { Duration, formatDuration, intervalToDuration, parse } from "date-fns";
import { DATE_RANGE_JOIN_BY } from "../constants";
import { ApiTimeBound } from "shared/types/ApiTimeBound";
import differenceInSeconds from "date-fns/differenceInSeconds";
import { DateRepresentationEnum } from "./enums";
import { DateTimeFormatType, DateTimeFormatDefault, DateTimeFormatEnGB } from "./dateTimeFormat";
import LanguageLocale from "shared/enums/LanguageLocale";
import enUS from "date-fns/locale/en-US";
import enGB from "date-fns/locale/en-GB";

class DateTimeHelper {
  /**Default formatting option for display date: MMM d, yyyy */
  private static defaultOptions = {
    showMonth: true,
    showYear: true
  };

  /**Default formatting option for input field: MMM d, yyyy p */
  static getFormattingOptionFormInputField(options?: DateFormatOptions) {
    return {
      showMonth: true,
      showYear: true,
      showTime: true,
      ...options
    };
  }

  /**Returns false if argument is Invalid Date and true otherwise. */
  static isValidDate(date: any) {
    return isValid(date);
  }

  /**
   * Given a date string returns Date Object.
   * Supporting Date as input as well to avoid if else in other functions
   **/
  static parseDate(date: Date | string | number) {
    if (this.isValidDate(date)) {
      return date as Date;
    }
    return parseISO(date as string);
  }

  /**
  Return the formatted date string in ISO 9075 format.
  @param date new Date("December 17, 1995 03:24:00")
  @param representation complete, date, time
  @example
  1: representation=complete => 1995-12-17 03:24:00 
  2: representation=date => 1995-12-17,
  3: representation=time => 03:24:00 
*/
  static getExactDate(
    date: Date | string | number,
    representation: DateRepresentationEnum = DateRepresentationEnum.COMPLETE
  ) {
    return formatISO9075(this.parseDate(date), { representation });
  }

  /**Based on locale returns locale file from date-fns library */
  private static getLocaleByLocaleId(localeId?: LanguageLocale) {
    switch (localeId) {
      case LanguageLocale.EN_US:
        return enUS;
      case LanguageLocale.EN_GB:
        return enGB;
      default:
        return enUS;
    }
  }

  /**Based on locale returns DateTime format enum */
  private static getDateTimeFormatEnumByLocalId(localeId?: LanguageLocale): DateTimeFormatType {
    switch (localeId) {
      case LanguageLocale.EN_GB:
        return new DateTimeFormatEnGB();
      case LanguageLocale.EN_US:
      default:
        return new DateTimeFormatDefault();
    }
  }

  /**Returns date-time formatting pattern depending on passed options */
  static getFormatString(options?: DateFormatOptions) {
    const { showWeekDay, showMonth, showYear, showTime, ...formattingOptions } = { ...this.defaultOptions, ...options };
    const DateTimeFormatEnumToUse = this.getDateTimeFormatEnumByLocalId(options?.localeId);

    //Show Only options
    if (formattingOptions.showOnlyTime) {
      return DateTimeFormatEnumToUse.TIME_ONLY;
    } else if (formattingOptions.showOnlyDate) {
      return DateTimeFormatEnumToUse.DATE_ONLY;
    } else if (formattingOptions.showOnlyMonth) {
      return DateTimeFormatEnumToUse.MONTH_ONLY;
    } else if (formattingOptions.showOnlyYear) {
      return DateTimeFormatEnumToUse.YEAR_ONLY;
    } else if (formattingOptions.showOnlyWeekDay) {
      return DateTimeFormatEnumToUse.WEEKDAY_ONLY;
    }

    //Normal options
    else if (showWeekDay && showMonth && showYear && showTime) {
      return DateTimeFormatEnumToUse.DATE_TIME_WITH_WEEKDAY;
    } else if (showWeekDay && showMonth && showYear) {
      return DateTimeFormatEnumToUse.DATE_WITH_WEEKDAY;
    } else if (showWeekDay && showMonth && showTime) {
      return DateTimeFormatEnumToUse.DATE_TIME_WITH_WEEKDAY_WITHOUT_YEAR;
    } else if (showMonth && showYear && showTime) {
      return DateTimeFormatEnumToUse.DATE_TIME;
    } else if (showWeekDay && showMonth) {
      return DateTimeFormatEnumToUse.DATE_WITH_WEEKDAY_WITHOUT_YEAR;
    } else if (showMonth && showTime) {
      return DateTimeFormatEnumToUse.DATE_TIME_WITHOUT_YEAR;
    } else if (showMonth && showYear) {
      return DateTimeFormatEnumToUse.DATE;
    } else if (showYear) {
      return DateTimeFormatEnumToUse.DATE_WITHOUT_MONTH;
    } else if (showMonth) {
      return DateTimeFormatEnumToUse.DATE_WITHOUT_YEAR;
    }

    return DateTimeFormatEnumToUse.DATE;
  }

  /**For September month we want to display "Sept" instead of "Sep" which is returned by library */
  private static fixSeptemberMonthName(displayDate: string) {
    //for September month we want to display Sept
    return displayDate.replace("Sep", "Sept");
  }

  /**Returns formatted date*/
  private static getFormattedDate(date: Date | string, option?: DateFormatOptions) {
    const dateToFormat = this.parseDate(date);
    const formattedString = format(dateToFormat, this.getFormatString(option), {
      locale: this.getLocaleByLocaleId(option?.localeId)
    });

    if (dateToFormat?.getMonth?.() === 8) {
      return this.fixSeptemberMonthName(formattedString);
    }
    return formattedString;
  }

  /**Returns formatted date range*/
  private static getFormattedRange(startDate: Date | string, endDate: Date | string, option?: DateFormatOptions) {
    const parsedStartDate = this.parseDate(startDate);
    const parsedEndDate = this.parseDate(endDate);

    if (option?.showTime || option?.showOnlyTime) {
      /* 
         When we display time in range then we have following scenarios
           1: Dates are same, then we can show it like : Jul 9, 2023 7:00 AM - 7:50 AM MT
           2: Dates are different, then we need to show both the dates like : Jul 10, 2023 4:00 PM - Jul 11, 2023 6:00 PM MT
         In case of showOnlyTime: Only time Range will be displayed (Even if dates are different)
      **/
      if (this.getDifferenceInCalendarDays(startDate, endDate) === 0) {
        //same date
        return [
          this.getFormattedDate(startDate, {
            ...option
          }),
          this.getFormattedDate(endDate, { ...option, showOnlyTime: true })
        ].join(DATE_RANGE_JOIN_BY);
      } else {
        return [
          this.getFormattedDate(startDate, {
            ...option
          }),
          this.getFormattedDate(endDate, { ...option })
        ].join(DATE_RANGE_JOIN_BY);
      }
    }

    if (parsedStartDate.getFullYear() !== parsedEndDate.getFullYear()) {
      //todo: if years is different then hideYear should hide year. similarly showOnly time, day, date etc should not be applicable
      return [
        this.getFormattedDate(startDate, { ...option, showYear: true }),
        this.getFormattedDate(endDate, { ...option, showYear: true })
      ].join(DATE_RANGE_JOIN_BY);
    }

    if (parsedStartDate.getMonth() !== parsedEndDate.getMonth()) {
      return [
        this.getFormattedDate(startDate, {
          ...option,
          showYear: false
        }),
        this.getFormattedDate(endDate, option)
      ].join(DATE_RANGE_JOIN_BY);
    }

    if (parsedStartDate.getDate() !== parsedEndDate.getDate()) {
      return [
        this.getFormattedDate(startDate, {
          ...option,
          showYear: false
        }),
        this.getFormattedDate(endDate, { ...option, showMonth: option?.showWeekDay ? true : false })
      ].join(DATE_RANGE_JOIN_BY);
    }

    return this.getFormattedDate(startDate, option);
  }

  /** If timezone is passed then append timezone string in formatted date */
  private static withTimezone(date: string, timeZone?: string) {
    return timeZone ? `${date} ${timeZone}` : date;
  }

  /**
   * Returns formatted date, if endDate is also passed then returns range.
   * @param DateFormatProps
   */
  static formatDate(args: DateFormatProps) {
    let displayDate;
    if (args.endDate) {
      // Start Date and End date passed then it is range.
      displayDate = this.getFormattedRange(args.startDate, args.endDate, args.option);
    } else {
      displayDate = this.getFormattedDate(args.startDate, args.option);
    }

    return this.withTimezone(displayDate, args.timeZone);
  }

  /**Return New date after added given duration */
  static addToDate(dateTime: Date | string, duration: Duration) {
    return this.getExactDate(add(this.parseDate(dateTime), duration));
  }

  /**
    Compare the two dates and return 1 if the first date is after the second,
    -1 if the first date is before the second or 0 if dates are equal.
    @param startDate new Date(1987, 1, 11),
    @param endDate new Date(1989, 6, 10)
    @returns -1
  */
  static compareDates(startDate: Date | string, endDate: Date | string) {
    return compareAsc(this.parseDate(startDate), this.parseDate(endDate));
  }

  /**
  Get the number of calendar days between the given dates.
  @param dateLeft new Date(2014, 6, 20)
  @param dateRight new Date(2014, 6, 5)
  @returns 15
*/
  static getDifferenceInCalendarDays(startDate: Date | string, endDate: Date | string) {
    return differenceInCalendarDays(this.parseDate(startDate), this.parseDate(endDate));
  }

  /**Returns no of seconds between given dates */
  static getDifferenceInSeconds(startDate: Date | string, endDate: Date | string) {
    return differenceInSeconds(this.parseDate(startDate), this.parseDate(endDate));
  }

  /**
  Given a date object and time returns  date + time string
  @param date = new Date("December 12, 1995 23:14:00")
  @param time 12:00:00
  @returns 1995-12-12 12:00:00
*/
  static getDateWithTime(date: Date | string, time: string) {
    return `${this.getExactDate(date, DateRepresentationEnum.DATE)} ${time}`;
  }

  /**Sort function for ApiTimeBound*/
  static sortByTime(date1: ApiTimeBound, date2: ApiTimeBound) {
    const fromCompare = this.compareDates(date1.timeFrom, date2.timeFrom);
    // time from is same then compare timeTo
    if (fromCompare === 0) {
      return this.compareDates(date1.timeTo, date2.timeTo);
    } else {
      return fromCompare;
    }
  }

  /**
   * Returns month name by month number
   * @param month
   * @example month = 0 => Jan
   */
  static getMonthByMonthName(month: number) {
    return this.fixSeptemberMonthName(
      this.formatDate({
        startDate: this.parseDate(new Date().setMonth(month, 1)),
        option: {
          showOnlyMonth: true
        }
      })
    );
  }

  /**
   * Return duration between start and end date in descriptive format
   * @param  Interval start: new Date("December 18, 1992 03:24:00") end: new Date("March 1, 1995 05:25:05")
   * @returns 2 years 2 months 11 days 2 hours 1 minute 5 seconds
   */
  static getDurationTime({ startDate, endDate }: { startDate: string; endDate: string }) {
    return formatDuration(
      intervalToDuration({
        start: this.parseDate(startDate),
        end: this.parseDate(endDate)
      })
    );
  }

  /**Returns Date object from formatted date string */
  static getParsedDateFromFormattedDate(dateString: string, dateFormat: string, referenceDate: Date = new Date()) {
    const date = parse(dateString, dateFormat, referenceDate);
    return isValid(date) ? date : referenceDate;
  }

  /**
   * returns date in splash format
   * @param date 2020-06-18 00:00:00
   * @returns 2020/06/18 00:00:00
   */
  static getDateInSlashFormat(date: string) {
    return date ? date.replace(/-/g, "/") : date;
  }

  static getDateTimeForReport() {
    return `${this.getExactDate(new Date()).split(":").join("_")}`;
  }

  static getSecondsToMinutesFormat(seconds: number) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
  }
}

export default DateTimeHelper;
